import { cartoRawTile } from "./mapTiles";

const W = 1080;
const H = 1920;
const MX = 60;
const MAP_BOTTOM = 1360;

const GREEN = "#52B788";
const GREEN_DARK = "#2D6A4F";
const AMBER = "#F59E0B";
const TERRA = "#E07A5F";
const BG = "#0F172A";
const TILE_BG = "#1B263B";
const TEXT = "#F8FAFC";
const MUTED = "#94A3B8";
const FAINT = "#64748B";

const loadImg = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const worldPx = (lat, lon, z) => {
  const s = 256 * 2 ** z;
  const x = ((lon + 180) / 360) * s;
  const sin = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * s;
  return [x, y];
};

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawZap = (ctx, cx, cy, size, color) => {
  const s = size / 24;
  ctx.save();
  ctx.translate(cx - 12 * s, cy - 12 * s);
  ctx.scale(s, s);
  ctx.beginPath();
  ctx.moveTo(13, 2);
  ctx.lineTo(3, 14);
  ctx.lineTo(12, 14);
  ctx.lineTo(11, 22);
  ctx.lineTo(21, 10);
  ctx.lineTo(12, 10);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
};

const wrapText = (ctx, text, maxWidth, maxLines = 2) => {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) cur = test;
    else {
      if (cur) lines.push(cur);
      cur = w;
      if (lines.length === maxLines) break;
    }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines && words.join(" ") !== lines.join(" ")) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 2) last = last.slice(0, -1);
    lines[maxLines - 1] = `${last}…`;
  }
  return lines;
};

const fmtDate = (s, withYear = false) => {
  try {
    return new Date(`${s}T12:00:00`).toLocaleDateString("nl-BE", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
  } catch {
    return s || "";
  }
};

const nl = (n) => Math.round(n || 0).toLocaleString("nl-BE");

const havKm = (a, b) => {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const drivenSliceEnd = (geometry, progress) => {
  if (!progress?.started) return 0;
  if (progress.finished) return geometry.length - 1;
  if (!progress.driven_km || progress.percent <= 0) return 0;
  const cum = [0];
  for (let i = 1; i < geometry.length; i++) cum.push(cum[i - 1] + havKm(geometry[i - 1], geometry[i]));
  const geomLen = cum[cum.length - 1];
  const target = progress.total_km > 0 ? progress.driven_km * (geomLen / progress.total_km) : progress.driven_km;
  for (let i = 1; i < cum.length; i++) if (cum[i] >= target) return i;
  return geometry.length - 1;
};

async function drawMap(ctx, trip, progress, mapTop) {
  const mapX = MX;
  const mapW = W - 2 * MX;
  const mapH = MAP_BOTTOM - mapTop;
  ctx.save();
  rr(ctx, mapX, mapTop, mapW, mapH, 36);
  ctx.clip();
  ctx.fillStyle = TILE_BG;
  ctx.fillRect(mapX, mapTop, mapW, mapH);

  const raw = trip.route?.geometry || [];
  const step = Math.max(1, Math.ceil(raw.length / 800));
  const geometry = raw.filter((_, i) => i % step === 0);
  if (raw.length && geometry[geometry.length - 1] !== raw[raw.length - 1]) geometry.push(raw[raw.length - 1]);

  if (geometry.length >= 2) {
    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    for (const [la, lo] of geometry) {
      if (la < minLat) minLat = la;
      if (la > maxLat) maxLat = la;
      if (lo < minLon) minLon = lo;
      if (lo > maxLon) maxLon = lo;
    }
    let zoom = 4;
    for (let z = 12; z >= 3; z--) {
      const [x1, y1] = worldPx(maxLat, minLon, z);
      const [x2, y2] = worldPx(minLat, maxLon, z);
      if (x2 - x1 <= mapW - 140 && y2 - y1 <= mapH - 140) { zoom = z; break; }
    }
    const [bx1, by1] = worldPx(maxLat, minLon, zoom);
    const [bx2, by2] = worldPx(minLat, maxLon, zoom);
    const cx = (bx1 + bx2) / 2;
    const cy = (by1 + by2) / 2;
    const tlx = cx - mapW / 2;
    const tly = cy - mapH / 2;
    const maxTile = 2 ** zoom;

    const tiles = [];
    for (let tx = Math.floor(tlx / 256); tx <= Math.floor((tlx + mapW) / 256); tx++) {
      for (let ty = Math.floor(tly / 256); ty <= Math.floor((tly + mapH) / 256); ty++) {
        if (ty < 0 || ty >= maxTile) continue;
        const wx = ((tx % maxTile) + maxTile) % maxTile;
        tiles.push({ tx, ty, url: cartoRawTile("dark_all", zoom, wx, ty, "abcd"[(tx + ty) % 4]) });
      }
    }
    const imgs = await Promise.all(tiles.map((t) => loadImg(t.url)));
    tiles.forEach((t, i) => {
      if (imgs[i]) ctx.drawImage(imgs[i], mapX + t.tx * 256 - tlx, mapTop + t.ty * 256 - tly, 256, 256);
    });

    const proj = geometry.map(([la, lo]) => {
      const [x, y] = worldPx(la, lo, zoom);
      return [mapX + x - tlx, mapTop + y - tly];
    });
    const projPt = (la, lo) => {
      const [x, y] = worldPx(la, lo, zoom);
      return [mapX + x - tlx, mapTop + y - tly];
    };
    const path = (pts) => {
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
    };
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    path(proj);
    ctx.strokeStyle = "rgba(15,23,42,0.85)";
    ctx.lineWidth = 13;
    ctx.stroke();

    const endIdx = drivenSliceEnd(geometry, progress);
    const underway = progress?.started && endIdx > 0;
    if (underway) {
      path(proj);
      ctx.strokeStyle = "#475569";
      ctx.lineWidth = 6;
      ctx.stroke();
      path(proj.slice(0, endIdx + 1));
      ctx.save();
      ctx.shadowColor = "rgba(82,183,136,0.75)";
      ctx.shadowBlur = 16;
      ctx.strokeStyle = GREEN;
      ctx.lineWidth = 8;
      ctx.stroke();
      ctx.restore();
    } else {
      path(proj);
      ctx.save();
      ctx.shadowColor = "rgba(82,183,136,0.6)";
      ctx.shadowBlur = 14;
      ctx.strokeStyle = GREEN;
      ctx.lineWidth = 8;
      ctx.stroke();
      ctx.restore();
    }

    for (const d of trip.days || []) {
      for (const cs of d.charging_stops || []) {
        if (cs.lat == null || cs.lon == null) continue;
        const [x, y] = projPt(cs.lat, cs.lon);
        ctx.beginPath();
        ctx.arc(x, y, 9, 0, Math.PI * 2);
        ctx.fillStyle = AMBER;
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = BG;
        ctx.stroke();
      }
    }

    const endpoint = (pt, ring) => {
      if (!pt || pt.lat == null) return;
      const [x, y] = projPt(pt.lat, pt.lon);
      ctx.beginPath();
      ctx.arc(x, y, 13, 0, Math.PI * 2);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();
      ctx.lineWidth = 5;
      ctx.strokeStyle = ring;
      ctx.stroke();
    };
    endpoint(trip.origin, GREEN);
    endpoint(trip.destination, TERRA);

    if (progress?.started && !progress.finished && progress.position) {
      const [x, y] = projPt(progress.position.lat, progress.position.lon);
      ctx.beginPath();
      ctx.arc(x, y, 24, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(82,183,136,0.4)";
      ctx.lineWidth = 8;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, 14, 0, Math.PI * 2);
      ctx.fillStyle = GREEN;
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#FFFFFF";
      ctx.stroke();
    }
  }

  ctx.font = "500 19px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.textAlign = "right";
  ctx.fillText("© OpenStreetMap © CARTO", mapX + mapW - 16, mapTop + mapH - 16);
  ctx.restore();

  rr(ctx, mapX, mapTop, mapW, mapH, 36);
  ctx.strokeStyle = "rgba(148,163,184,0.25)";
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawProgressBand(ctx, trip, progress, y) {
  const x = MX;
  const w = W - 2 * MX;
  ctx.textAlign = "left";
  if (progress?.started) {
    const day = progress.finished ? progress.days_total : Math.min((progress.days_done || 0) + 1, progress.days_total);
    ctx.font = "800 26px Manrope, sans-serif";
    ctx.fillStyle = GREEN;
    try { ctx.letterSpacing = "4px"; } catch { /* noop */ }
    ctx.fillText(progress.finished ? "REIS VOLTOOID" : `ONDERWEG — DAG ${day} VAN ${progress.days_total}`, x, y);
    try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
    ctx.font = "500 32px 'JetBrains Mono', monospace";
    ctx.fillStyle = TEXT;
    ctx.textAlign = "right";
    ctx.fillText(`${progress.percent}%`, x + w, y);
    ctx.textAlign = "left";

    const barY = y + 24;
    rr(ctx, x, barY, w, 18, 9);
    ctx.fillStyle = "#1E293B";
    ctx.fill();
    const fillW = Math.max(18, (w * Math.min(progress.percent, 100)) / 100);
    const grad = ctx.createLinearGradient(x, 0, x + fillW, 0);
    grad.addColorStop(0, GREEN_DARK);
    grad.addColorStop(1, GREEN);
    rr(ctx, x, barY, fillW, 18, 9);
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.font = "600 30px Figtree, sans-serif";
    ctx.fillStyle = MUTED;
    const rest = Math.max(0, (progress.total_km || 0) - (progress.driven_km || 0));
    ctx.fillText(
      progress.finished
        ? `${nl(progress.total_km)} km gereden in ${progress.days_total} dagen`
        : `${nl(progress.driven_km)} km gereden · nog ${nl(rest)} km te gaan`,
      x, barY + 58
    );
  } else {
    ctx.font = "800 26px Manrope, sans-serif";
    ctx.fillStyle = GREEN;
    try { ctx.letterSpacing = "4px"; } catch { /* noop */ }
    ctx.fillText("BINNENKORT ONDERWEG", x, y);
    try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
    ctx.font = "600 32px Figtree, sans-serif";
    ctx.fillStyle = MUTED;
    ctx.fillText(`Vertrek op ${fmtDate(trip.start_date, true)}`, x, y + 52);
  }
}

function drawStats(ctx, trip, y) {
  const gap = 32;
  const tw = (W - 2 * MX - 2 * gap) / 3;
  const stops = (trip.days || []).reduce((s, d) => s + (d.charging_stops || []).length, 0);
  const stats = [
    { v: nl(trip.route?.distance_km), suffix: " km", label: "AFSTAND" },
    { v: String((trip.days || []).length), suffix: "", label: "REISDAGEN" },
    { v: String(stops), suffix: "", label: "LAADSTOPS" },
  ];
  stats.forEach((s, i) => {
    const x = MX + i * (tw + gap);
    rr(ctx, x, y, tw, 150, 24);
    ctx.fillStyle = "#16202F";
    ctx.fill();
    ctx.strokeStyle = "rgba(148,163,184,0.15)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.textAlign = "center";
    ctx.font = "500 52px 'JetBrains Mono', monospace";
    ctx.fillStyle = TEXT;
    const suffixW = s.suffix ? ctx.measureText(s.suffix).width * 0.55 : 0;
    ctx.fillText(s.v, x + tw / 2 - suffixW / 2, y + 68);
    if (s.suffix) {
      ctx.font = "500 30px 'JetBrains Mono', monospace";
      ctx.fillStyle = MUTED;
      ctx.fillText(s.suffix.trim(), x + tw / 2 + ctx.measureText(s.v).width * 0.87 + 6, y + 66);
    }
    ctx.font = "800 21px Manrope, sans-serif";
    ctx.fillStyle = FAINT;
    try { ctx.letterSpacing = "3px"; } catch { /* noop */ }
    ctx.fillText(s.label, x + tw / 2, y + 116);
    try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
  });
  ctx.textAlign = "left";
}

function drawFooter(ctx, shareUrl, y) {
  ctx.beginPath();
  ctx.moveTo(MX, y - 44);
  ctx.lineTo(W - MX, y - 44);
  ctx.strokeStyle = "rgba(148,163,184,0.15)";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.textAlign = "left";
  if (shareUrl) {
    ctx.font = "600 26px Figtree, sans-serif";
    ctx.fillStyle = FAINT;
    ctx.fillText("Volg deze reis live:", MX, y);
    ctx.font = "800 31px Manrope, sans-serif";
    ctx.fillStyle = GREEN;
    let short = shareUrl.replace(/^https?:\/\//, "");
    let size = 31;
    while (size > 20 && ctx.measureText(short).width > 640) {
      size -= 1;
      ctx.font = `800 ${size}px Manrope, sans-serif`;
    }
    if (ctx.measureText(short).width > 640) {
      while (ctx.measureText(`${short}…`).width > 640 && short.length > 10) short = short.slice(0, -2);
      short = `${short}…`;
    }
    ctx.fillText(short, MX, y + 42);
  } else {
    ctx.font = "600 28px Figtree, sans-serif";
    ctx.fillStyle = FAINT;
    ctx.fillText("Slim gepland, elektrisch gereden", MX, y + 20);
  }

  const bx = W - MX;
  ctx.textAlign = "right";
  ctx.font = "800 34px Manrope, sans-serif";
  ctx.fillStyle = TEXT;
  ctx.fillText("VoltRoute", bx, y + 26);
  const tw = ctx.measureText("VoltRoute").width;
  ctx.beginPath();
  ctx.arc(bx - tw - 34, y + 14, 24, 0, Math.PI * 2);
  ctx.fillStyle = GREEN_DARK;
  ctx.fill();
  drawZap(ctx, bx - tw - 34, y + 14, 26, "#FFFFFF");
  ctx.textAlign = "left";
}

export async function generateTripPoster(trip, { progress = null, shareUrl = null } = {}) {
  try {
    await Promise.all([
      document.fonts.load("800 80px Manrope"),
      document.fonts.load("600 36px Figtree"),
      document.fonts.load("500 52px 'JetBrains Mono'"),
      document.fonts.ready,
    ]);
  } catch { /* noop */ }

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  ctx.beginPath();
  ctx.arc(MX + 26, 104, 26, 0, Math.PI * 2);
  ctx.fillStyle = GREEN_DARK;
  ctx.fill();
  drawZap(ctx, MX + 26, 104, 28, "#FFFFFF");
  ctx.font = "800 30px Manrope, sans-serif";
  ctx.fillStyle = MUTED;
  try { ctx.letterSpacing = "8px"; } catch { /* noop */ }
  ctx.textAlign = "left";
  ctx.fillText("VOLTROUTE", MX + 70, 115);
  try { ctx.letterSpacing = "0px"; } catch { /* noop */ }

  ctx.font = "800 76px Manrope, sans-serif";
  const nameLines = wrapText(ctx, trip.name || "Onze roadtrip", W - 2 * MX);
  let cursor = 232;
  ctx.fillStyle = TEXT;
  for (const line of nameLines) {
    ctx.fillText(line, MX, cursor);
    cursor += 88;
  }
  cursor -= 20;

  const o = (trip.origin?.name || "").split(",")[0];
  const d = (trip.destination?.name || "").split(",")[0];
  ctx.font = "600 36px Figtree, sans-serif";
  ctx.fillStyle = MUTED;
  const routeStr = `${o}  →  ${d}${trip.round_trip ? "  →  terug" : ""}`;
  ctx.fillText(wrapText(ctx, routeStr, W - 2 * MX, 1)[0] || "", MX, cursor);
  cursor += 52;

  ctx.font = "500 28px 'JetBrains Mono', monospace";
  ctx.fillStyle = FAINT;
  ctx.fillText(`${fmtDate(trip.start_date)} — ${fmtDate(trip.end_date, true)}`, MX, cursor);

  const mapTop = cursor + 40;
  await drawMap(ctx, trip, progress, mapTop);

  drawProgressBand(ctx, trip, progress, 1436);
  drawStats(ctx, trip, 1580);
  drawFooter(ctx, shareUrl, 1806);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Poster renderen mislukt"))), "image/png");
  });
}
